import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  COMPANY_DOCUMENT_MAX_BYTES,
  getCompanyDocumentSignedUrl,
  listCompanyDocuments,
  uploadCompanyDocument,
  type CompanyDocument,
} from '@/api/company-documents'
import { Button } from '@/components/ui/Button'
import { formatMoscowDateTime } from '@/utils/format'
import i18n from '@/utils/i18n'

/**
 * Раздел «Документы» в карточке компании.
 *
 * Назначение: загрузка подписанных соглашений и иных документов админами.
 * Удаления нет — по RLS политики DELETE для таблицы и bucket'а отсутствуют.
 *
 * См. `src/api/company-documents.ts` и миграцию `20260526120000_company_documents.sql`.
 */
export interface CompanyDocumentsSectionProps {
  companyId: string
  /**
   * Может ли текущий пользователь загружать (только admin). Если false —
   * показываем секцию в режиме просмотра без кнопки загрузки.
   */
  canUpload: boolean
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} ${i18n.t('companies.docs.unitB')}`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} ${i18n.t('companies.docs.unitKb')}`
  return `${(n / (1024 * 1024)).toFixed(1)} ${i18n.t('companies.docs.unitMb')}`
}

export function CompanyDocumentsSection({
  companyId,
  canUpload,
}: CompanyDocumentsSectionProps) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data: docs, isLoading } = useQuery({
    queryKey: ['company-documents', companyId],
    queryFn: () => listCompanyDocuments(companyId),
    enabled: !!companyId,
  })

  const [formOpen, setFormOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error(i18n.t('companies.docs.chooseError'))
      await uploadCompanyDocument(companyId, file, description)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company-documents', companyId] })
      setFile(null)
      setDescription('')
      setFormOpen(false)
      setError(null)
    },
    onError: (e: Error) => setError(e.message),
  })

  const canSubmit =
    !!file &&
    description.trim().length > 0 &&
    file.size <= COMPANY_DOCUMENT_MAX_BYTES &&
    !mut.isPending

  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h2 className="stryzh-heading-card">{t('companies.docs.title')}</h2>
          <p className="mt-1 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
            {t('companies.docs.subtitle')}
          </p>
        </div>
        {canUpload && !formOpen ? (
          <Button variant="primary" onClick={() => setFormOpen(true)}>
            {t('companies.docs.upload')}
          </Button>
        ) : null}
      </div>

      {formOpen ? (
        <div className="mb-4 rounded-lg border border-[var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-border-subtle)] p-3">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <span className="stryzh-input-label">{t('companies.docs.fileLabel')}</span>
              <div className="flex flex-wrap items-center gap-2">
                <label className="cursor-pointer text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-accent-orange)] hover:underline">
                  {file ? t('companies.docs.replace') : t('companies.docs.choose')}
                  <input
                    type="file"
                    disabled={mut.isPending}
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null
                      setError(null)
                      if (f && f.size > COMPANY_DOCUMENT_MAX_BYTES) {
                        setError(t('companies.docs.tooLarge'))
                        setFile(null)
                      } else {
                        setFile(f)
                      }
                      e.target.value = ''
                    }}
                    className="hidden"
                  />
                </label>
                {file ? (
                  <span className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
                    {file.name} · {formatBytes(file.size)}
                  </span>
                ) : (
                  <span className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                    {t('companies.docs.hint')}
                  </span>
                )}
              </div>
            </div>

            <label className="flex flex-col gap-1">
              <span className="stryzh-input-label !inline-flex items-center gap-3 leading-none">
                <span
                  aria-hidden
                  title={t('companies.docs.required')}
                  className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--stryzh-color-accent-orange)]"
                />
                <span>{t('companies.docs.description')}</span>
              </span>
              <textarea
                className="stryzh-input-field min-h-[72px] resize-y"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('companies.docs.descriptionPlaceholder')}
                disabled={mut.isPending}
              />
            </label>

            {error ? (
              <span className="stryzh-input-error-text">{error}</span>
            ) : null}

            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setFormOpen(false)
                  setFile(null)
                  setDescription('')
                  setError(null)
                }}
                disabled={mut.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => mut.mutate()}
                disabled={!canSubmit}
              >
                {mut.isPending ? t('companies.docs.uploading') : t('companies.docs.uploadBtn')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('common.loading')}
        </p>
      ) : (docs?.length ?? 0) === 0 ? (
        <p className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('common.noData')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {docs!.map((d) => (
            <DocumentRow key={d.id} doc={d} />
          ))}
        </ul>
      )}
    </>
  )
}

function DocumentRow({ doc }: { doc: CompanyDocument }) {
  const { t } = useTranslation()
  const [downloading, setDownloading] = useState(false)

  async function handleDownload() {
    setDownloading(true)
    try {
      const url = await getCompanyDocumentSignedUrl(doc.file_path)
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <li className="rounded-lg border border-[var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-border-subtle)] p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="break-all text-left text-[length:var(--stryzh-font-size-sm)] font-medium text-[color:var(--stryzh-color-accent-orange)] hover:underline disabled:opacity-60"
          >
            {doc.file_name}
          </button>
          <p className="mt-1 break-words text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)]">
            {doc.description}
          </p>
          <p className="mt-2 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
            {formatMoscowDateTime(doc.uploaded_at)} {t('companies.docs.msk')}
            {doc.uploaded_by_email ? ` · ${doc.uploaded_by_email}` : ''}
            {' · '}
            {formatBytes(doc.file_size)}
          </p>
        </div>
        <Button
          variant="ghost"
          onClick={handleDownload}
          disabled={downloading}
          aria-label={t('companies.docs.download')}
        >
          {downloading ? '…' : t('companies.docs.download')}
        </Button>
      </div>
    </li>
  )
}
